import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem } from './ui/carousel';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { getHeroCarouselItems } from '../api';

const AUTOPLAY_INTERVAL = 5000; // 5 seconds

export default function HeroCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [api, setApi] = useState(null);

  useEffect(() => {
    getHeroCarouselItems()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (!api || items.length === 0) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, AUTOPLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [api, items.length]);

  if (loading) {
    return (
      <div className="w-full h-[320px] md:h-[480px] bg-muted/30">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (items.length === 0) {
    return <PlaceholderCarousel />;
  }

  return (
    <div className="relative w-full overflow-hidden">
      <Carousel
        className="w-full"
        opts={{
          loop: true,
          align: 'center',
          duration: 400,
        }}
        setApi={(carouselApi) => {
          setApi(carouselApi);
          if (carouselApi) {
            carouselApi.on('select', () => {
              setCurrentSlide(carouselApi.selectedScrollSnap());
            });
          }
        }}
      >
        <CarouselContent>
          {items.map((slide) => (
            <CarouselItem key={slide._id}>
              <SlideContent slide={slide} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Dots indicator */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentSlide === index ? 'w-6 bg-brand-red' : 'w-2 bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function SlideContent({ slide }) {
  const navigate = useNavigate();

  return (
    <CarouselItem>
      <div className="relative h-[320px] md:h-[480px] w-full">
        <img
          src={slide.image}
          alt={slide.title}
          className="w-full h-full object-cover"
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 md:pb-16 px-4 text-center">
          <h2 className="text-2xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">
            {slide.title}
          </h2>
          {slide.subtitle && (
            <p className="text-sm md:text-lg text-white/90 mb-4 drop-shadow">
              {slide.subtitle}
            </p>
          )}
          <Button
            onClick={() => navigate(slide.link || '/shop')}
            size="lg"
            className="bg-brand-red hover:bg-red-700 text-white font-semibold px-8"
          >
            Shop Now
          </Button>
        </div>
      </div>
    </CarouselItem>
  );
}

function PlaceholderCarousel() {

  const PLACEHOLDER_IMAGES = [
    {
      id: 1,
      image: '/hero-1.jpg',
      title: 'OEM Parts Available Now',
      subtitle: 'In partnership with Motos Illimitees',
      link: '/shop',
    },
    {
      id: 2,
      image: '/hero-2.png',
      title: 'Premium Motorcycle Gear',
      subtitle: 'Top brands, best prices',
      link: '/shop?partType=Apparel',
    },
    {
      id: 3,
      image: '/hero-3.jpg',
      title: 'Weekly Hot Deals',
      subtitle: 'Save on essential parts',
      link: '/shop?hotDeal=true',
    },
    {
      id: 4,
      image: '/hero-4.png',
      title: 'Expert Motorcycle Repair',
      subtitle: 'Trusted service for your ride',
      link: '/shop',
    },
    {
      id: 5,
      image: '/hero-5.jpg',
      title: 'Shop Online Now',
      subtitle: 'Free shipping on orders over $99',
      link: '/shop',
    },
  ];

  return (
    <div className="relative w-full overflow-hidden">
      <Carousel
        className="w-full"
        opts={{ loop: true, align: 'center', duration: 400 }}
      >
        <CarouselContent>
          {PLACEHOLDER_IMAGES.map((slide) => (
            <CarouselItem key={slide.id}>
              <div className="relative h-[320px] md:h-[480px] w-full">
                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 md:pb-16 px-4 text-center">
                  <h2 className="text-2xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                    {slide.title}
                  </h2>
                  {slide.subtitle && (
                    <p className="text-sm md:text-lg text-white/90 mb-4 drop-shadow">{slide.subtitle}</p>
                  )}
                  <Button
                    asChild
                    size="lg"
                    className="bg-brand-red hover:bg-red-700 text-white font-semibold px-8"
                  >
                    <a href={slide.link}>Shop Now</a>
                  </Button>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {PLACEHOLDER_IMAGES.map((_, index) => (
          <button
            key={index}
            className={`h-2 rounded-full transition-all ${
              index === 0 ? 'w-6 bg-brand-red' : 'w-2 bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
}